/* eslint-disable no-unused-vars */
import { useState, useEffect } from "react";
import KhwarizmChat from "./KhwarizmChat";

const t = {
  en: {
    nav: { home: "Home", services: "Services", portfolio: "Portfolio", about: "About", contact: "Contact" },
    hero: {
      tagline: "THE BALANCE OF DISCOVERY",
      subtitle: "From the Legacy of Al-Khwarizmi",
      title: "Where Algorithms\nMeet Ambition",
      desc: "Market Intelligence · AI Production · Automation · Content Engine",
      cta: "Explore Our World",
      quote: '"In every problem, two sides held in equilibrium." — Al-Khwarizmi, c. 820'
    },
    services: {
      title: "What We Build",
      subtitle: "Disciplines powered by algorithmic intelligence",
      items: [
        { icon: `<svg width="36" height="36" viewBox="0 0 36 36" fill="none" stroke="#C8963E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="26" width="5" height="8" rx="1"/><rect x="12" y="18" width="5" height="16" rx="1"/><rect x="20" y="12" width="5" height="22" rx="1"/><rect x="28" y="6" width="5" height="28" rx="1"/><path d="M4 8l7 6 8-8 13 6" strokeWidth="1.8"/><circle cx="4" cy="8" r="1.5" fill="#C8963E"/><circle cx="11" cy="14" r="1.5" fill="#C8963E"/><circle cx="19" cy="6" r="1.5" fill="#C8963E"/><circle cx="32" cy="12" r="1.5" fill="#C8963E"/></svg>`, title: "Market Intelligence", desc: "Automated market analysis delivered around the clock. Signals, patterns, and insights — powered by algorithms, not emotions." },
        { icon: `<svg width="36" height="36" viewBox="0 0 36 36" fill="none" stroke="#C8963E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="8" width="30" height="22" rx="2"/><path d="M3 16h30"/><path d="M10 8v8"/><path d="M17 8v8"/><path d="M24 8v8"/><path d="M6.5 8l3.5-4"/><path d="M13.5 8l3.5-4"/><path d="M20.5 8l3.5-4"/><path d="M27.5 8l3.5-4"/><circle cx="18" cy="23" r="3"/><polygon points="17,21.5 20,23 17,24.5" fill="#C8963E" stroke="none"/></svg>`, title: "AI Production", desc: "End-to-end cinematic production by AI. Script, visuals, voiceover, editing, and delivery — fully automated, zero human touch." },
        { icon: `<svg width="36" height="36" viewBox="0 0 36 36" fill="none" stroke="#C8963E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="18" r="14"/><path d="M19 6l-5 14h8l-5 14" strokeWidth="2"/></svg>`, title: "Workflow Automation", desc: "Connect your tools. Automate your tasks. From email responses to data pipelines — systems that never sleep." },
        { icon: `<svg width="36" height="36" viewBox="0 0 36 36" fill="none" stroke="#C8963E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M8 28l-3 3 3-1 20-20-2-2z"/><path d="M22 8l4 0 2 2 0 4"/><path d="M6 22l8-8"/><circle cx="28" cy="8" r="5"/><path d="M14 32h16"/></svg>`, title: "Content Engine", desc: "AI-generated content across all platforms. Threads, captions, articles, and newsletters — written, scheduled, and published automatically." }
      ]
    },
    portfolio: {
      title: "Our Work",
      subtitle: "Selected projects from the Khwarizm universe",
      items: [
        { tag: "MARKET INTELLIGENCE", title: "Autonomous Trading Bot", desc: "24/7 algorithmic trading with real-time market analysis" },
        { tag: "AI PRODUCTION", title: "The Algorithm of Destiny", desc: "AI-generated cinematic short film — 6 scenes, full production" },
        { tag: "AUTOMATION", title: "Smart Workflow Engine", desc: "Intelligent automation engine that connects your tools and workflows seamlessly" },
        { tag: "CONTENT ENGINE", title: "Khwarizm Media", desc: "Multi-platform content strategy across YouTube, X, Instagram, TikTok" }
      ]
    },
    about: {
      title: "The Origin",
      p1: "Named after Abu Abdullah Muhammad ibn Musa al-Khwarizmi — the 9th-century scholar of the House of Wisdom whose work on al-jabr gave rise to the words algebra and algorithm.",
      p2: "He understood every problem as a question of balance: two sides of an equation, held in equilibrium until truth could pass between them.",
      p3: "1200 years later, we carry his legacy forward. Khwarizm is not just a brand — it's a philosophy: systematic transformation of chaos into order, step by step.",
      quote: "Balance both sides. Then solve."
    },
    process: {
      title: "Our Methodology",
      subtitle: "From idea to impact, in four deliberate steps",
      items: [
        { num: "1", title: "Discovery", desc: "We listen, study, and map the terrain. Every project begins with deep understanding of your vision and constraints." },
        { num: "2", title: "Strategy", desc: "We design the algorithm. A clear roadmap that balances ambition with execution, creativity with precision." },
        { num: "3", title: "Build", desc: "We craft, code, and create. Where ideas take shape through careful iteration and intelligent systems." },
        { num: "4", title: "Deliver", desc: "We launch, refine, and support. The work doesn't end at delivery — it begins there." }
      ]
    },
    contact: {
      title: "Start the Equation",
      subtitle: "Ready to build something extraordinary?",
      email: "Email", name: "Name", message: "Message", send: "Send Message",
      social: "Find us everywhere"
    },
    footer: { tagline: "Code. Culture. Conquer.", rights: "All rights reserved.", est: "EST · 2026", disclaimer: "Educational market insights only. Not financial or trading advice." }
  },
  ar: {
    nav: { home: "الرئيسية", services: "الخدمات", portfolio: "الأعمال", about: "عن خوارزم", contact: "تواصل معنا" },
    hero: {
      tagline: "اتزان الاكتشاف",
      subtitle: "من إرث الخوارزمي",
      title: "حيث تلتقي الخوارزميات بالطموح",
      desc: "ذكاء السوق · إنتاج بالـ AI · أتمتة · محرك المحتوى",
      cta: "استكشف عالم خوارزم",
      quote: '"في كل مسألة طرفان، وبينهما اتزان." — مستوحى من إرث الخوارزمي'
    },
    services: {
      title: "ما الذي نبنيه",
      subtitle: "مجالات تُدار بمنطق الخوارزميات وقوة الذكاء الاصطناعي",
      items: [
        { icon: `<svg width="36" height="36" viewBox="0 0 36 36" fill="none" stroke="#C8963E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="26" width="5" height="8" rx="1"/><rect x="12" y="18" width="5" height="16" rx="1"/><rect x="20" y="12" width="5" height="22" rx="1"/><rect x="28" y="6" width="5" height="28" rx="1"/><path d="M4 8l7 6 8-8 13 6" strokeWidth="1.8"/><circle cx="4" cy="8" r="1.5" fill="#C8963E"/><circle cx="11" cy="14" r="1.5" fill="#C8963E"/><circle cx="19" cy="6" r="1.5" fill="#C8963E"/><circle cx="32" cy="12" r="1.5" fill="#C8963E"/></svg>`, title: "ذكاء السوق", desc: "تحليل آلي للأسواق يعمل على مدار الساعة. إشارات وأنماط ورؤى تحليلية مبنية على الخوارزميات، لا على العواطف." },
        { icon: `<svg width="36" height="36" viewBox="0 0 36 36" fill="none" stroke="#C8963E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="8" width="30" height="22" rx="2"/><path d="M3 16h30"/><path d="M10 8v8"/><path d="M17 8v8"/><path d="M24 8v8"/><path d="M6.5 8l3.5-4"/><path d="M13.5 8l3.5-4"/><path d="M20.5 8l3.5-4"/><path d="M27.5 8l3.5-4"/><circle cx="18" cy="23" r="3"/><polygon points="17,21.5 20,23 17,24.5" fill="#C8963E" stroke="none"/></svg>`, title: "الإنتاج بالذكاء الاصطناعي", desc: "إنتاج سينمائي متكامل بالذكاء الاصطناعي. من السيناريو إلى التعليق الصوتي والمونتاج والتسليم — بدون أي تدخل بشري." },
        { icon: `<svg width="36" height="36" viewBox="0 0 36 36" fill="none" stroke="#C8963E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="18" r="14"/><path d="M19 6l-5 14h8l-5 14" strokeWidth="2"/></svg>`, title: "أتمتة العمليات", desc: "اربط أدواتك وأتمت مهامك. من الردود على البريد إلى معالجة البيانات — أنظمة تعمل بلا توقف." },
        { icon: `<svg width="36" height="36" viewBox="0 0 36 36" fill="none" stroke="#C8963E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M8 28l-3 3 3-1 20-20-2-2z"/><path d="M22 8l4 0 2 2 0 4"/><path d="M6 22l8-8"/><circle cx="28" cy="8" r="5"/><path d="M14 32h16"/></svg>`, title: "محرك المحتوى", desc: "محتوى يُنتَج بالذكاء الاصطناعي لجميع المنصات. ثريدات ومقالات ونشرات تُكتَب وتُجدوَل وتُنشَر تلقائيًا." }
      ]
    },
    portfolio: {
      title: "أعمالنا",
      subtitle: "نماذج مختارة من عالم خوارزم",
      items: [
        { tag: "ذكاء السوق", title: "بوت تداول آلي", desc: "نظام تداول خوارزمي يعمل على مدار الساعة مع تحليل لحظي لحركة السوق." },
        { tag: "إنتاج بالـ AI", title: "خوارزمية القدر", desc: "فيلم قصير مولّد بالذكاء الاصطناعي بأسلوب سينمائي متكامل." },
        { tag: "أتمتة", title: "محرك أتمتة ذكي", desc: "نظام أتمتة ذكي يربط أدواتك ومهامك اليومية بسلاسة تامة." },
        { tag: "محرك المحتوى", title: "خوارزم ميديا", desc: "استراتيجية محتوى متعددة المنصات تشمل يوتيوب وX وإنستغرام وتيك توك." }
      ]
    },
    about: {
      title: "الأصل",
      p1: "استُلهم اسم خوارزم من أبي عبد الله محمد بن موسى الخوارزمي، عالم بيت الحكمة في القرن التاسع، وأحد الأسماء التي ارتبطت بالجبر وبدايات التفكير الخوارزمي.",
      p2: "كان جوهر فكرته أن كل مسألة يمكن فهمها عبر النظام والاتزان: طرفان في معادلة، وخطوات واضحة تقود إلى الحل.",
      p3: "بعد أكثر من ١٢٠٠ عام، يحمل خوارزم هذا الإرث إلى العصر الحديث: تحويل الفوضى إلى نظام، والبيانات إلى قرارات، والطموح إلى بناء حقيقي، خطوة بخطوة.",
      quote: "وازن الطرفين، ثم ابدأ الحل."
    },
    process: {
      title: "منهجيتنا",
      subtitle: "من الفكرة إلى الأثر، في أربع خطوات مدروسة",
      items: [
        { num: "1", title: "الاستكشاف", desc: "نستمع، ندرس، ونرسم خريطة المشروع. كل عمل يبدأ بفهم عميق لرؤيتك وإمكاناتك." },
        { num: "2", title: "الاستراتيجية", desc: "نصمم الخوارزمية. خارطة طريق واضحة توازن بين الطموح والتنفيذ، بين الإبداع والدقة." },
        { num: "3", title: "البناء", desc: "نصنع، نبرمج، نبتكر. هنا تأخذ الأفكار شكلها عبر تكرار مدروس وأنظمة ذكية." },
        { num: "4", title: "التسليم", desc: "نطلق، نحسّن، ندعم. العمل لا ينتهي بالتسليم — بل يبدأ منه." }
      ]
    },
    contact: {
      title: "ابدأ المعادلة",
      subtitle: "هل أنت مستعد لبناء شيء استثنائي؟",
      email: "البريد الإلكتروني", name: "الاسم", message: "الرسالة", send: "إرسال الرسالة",
      social: "تجدنا على المنصات"
    },
    footer: { tagline: "نظام. ذكاء. أثر.", rights: "جميع الحقوق محفوظة.", est: "تأسست · ٢٠٢٦", disclaimer: "قراءات السوق تعليمية فقط. ليست نصيحة مالية أو توصية تداول." }
  }
};

/* Production: place khwarizm-logo.png in /public/ folder */
const LOGO_SRC = "/khwarizm-logo.png";

const Logo = ({size=48}) => (
  <img src={LOGO_SRC} alt="Khwarizm" width={size} height={size} style={{objectFit: "contain", display: "block"}} />
);

export default function App() {
  const [lang, setLang] = useState("en");
  const [sy, setSy] = useState(0);
  const [sec, setSec] = useState("home");
  const [on, setOn] = useState(false);
  const [mob, setMob] = useState(false); /* mobile menu toggle */
  const [showBtt, setShowBtt] = useState(false);
  const [sacredAngle, setSacredAngle] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [logoPhase, setLogoPhase] = useState(0); // 0=equation, 1=dissolve, 2=logo, 3=settled
  const [light, setLight] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [codeLines, setCodeLines] = useState([]);
  const [codeCursor, setCodeCursor] = useState(true)
  
  const c = t[lang];
  const r = lang === "ar";

  const serviceDetails = {
    en: [
      {
        title: "Market Intelligence",
        subtitle: "See the market before it moves",
        desc: "Our AI systems continuously scan global markets, analyzing thousands of data points in real-time to deliver actionable insights. No human bias. No emotional trading. Just pure algorithmic clarity.",
        features: [
          "24/7 automated market scanning and pattern recognition",
          "Real-time signal detection across multiple asset classes",
          "Daily and weekly AI-generated analysis reports",
          "Custom alerts delivered via Telegram, email, or WhatsApp",
          "Historical pattern matching with predictive modeling"
        ],
        example: "Every morning at 6 AM, you receive a comprehensive market brief — trends, anomalies, and opportunities — all analyzed and written entirely by AI."
      },
      {
        title: "AI Production",
        subtitle: "From script to screen, fully automated",
        desc: "Complete cinematic production powered entirely by artificial intelligence. Our systems handle every stage — writing, visual generation, voice synthesis, editing, and final delivery — without any human intervention.",
        features: [
          "AI-generated scripts and storyboards",
          "Automated visual generation using cutting-edge models",
          "Natural voiceover synthesis in multiple languages",
          "Intelligent editing with pacing and music selection",
          "Final render and delivery in broadcast-ready formats"
        ],
        example: "You describe your vision in one sentence. Within hours, you receive a fully produced video — scripted, voiced, edited, and ready to publish."
      },
      {
        title: "Workflow Automation",
        subtitle: "Systems that never sleep",
        desc: "We build intelligent automation pipelines that connect your tools, eliminate repetitive tasks, and keep your operations running around the clock. Every workflow is designed to operate independently — no monitoring required.",
        features: [
          "Email auto-responses and intelligent routing",
          "Data collection, processing, and reporting pipelines",
          "Cross-platform integration (CRM, email, social, databases)",
          "Scheduled task execution with error handling",
          "Self-monitoring systems with automatic recovery"
        ],
        example: "A client sends an inquiry at 3 AM. The system reads it, categorizes it, drafts a personalized response, and schedules a follow-up — all before sunrise."
      },
      {
        title: "Content Engine",
        subtitle: "Your brand voice, amplified by AI",
        desc: "A fully autonomous content production system. Our AI writes, designs, schedules, and publishes content across all your platforms — maintaining your brand voice while operating entirely on autopilot.",
        features: [
          "AI-written threads, captions, articles, and newsletters",
          "Automatic scheduling and publishing across all platforms",
          "Brand voice training — the AI learns your tone and style",
          "Performance analytics with content optimization",
          "Multi-language content generation (Arabic and English)"
        ],
        example: "Your X account posts a daily thread at 9 AM, your Instagram gets a carousel at noon, and your newsletter goes out every Thursday — all without you touching a keyboard."
      },
      {
        title: "AI Agents",
        subtitle: "Digital employees that work 24/7",
        desc: "Custom-built autonomous agents that handle real business tasks. From customer support to data analysis to decision-making — these digital workers operate independently, learn from interactions, and improve over time.",
        features: [
          "Intelligent customer support via chat, email, and voice",
          "Autonomous data analysis and report generation",
          "Real-time monitoring of systems, markets, and competitors",
          "Task delegation and workflow orchestration",
          "Continuous learning and self-improvement"
        ],
        example: "Your AI agent handles 200 customer inquiries per day, escalates complex issues, updates your CRM, and sends you a summary every evening — completely autonomously."
      },
      {
        title: "AI Academy",
        subtitle: "From zero to full automation",
        desc: "Comprehensive learning programs designed to teach you how to build, deploy, and monetize AI systems. Whether you are a beginner or an experienced developer, our curriculum takes you from understanding AI to profiting from it.",
        features: [
          "Structured courses from beginner to advanced",
          "Hands-on projects building real AI agents",
          "Live workshops and recorded masterclasses",
          "Community access with peer learning and support",
          "Certification upon course completion"
        ],
        example: "In 8 weeks, you go from knowing nothing about AI to deploying your first autonomous agent that generates revenue while you sleep."
      }
    ],
    ar: [
      {
        title: "ذكاء السوق",
        subtitle: "اقرأ السوق قبل أن يتحرّك",
        desc: "أنظمتنا الذكية تراقب الأسواق العالمية باستمرار، وتحلّل آلاف البيانات لحظيًا لتقديم رؤى قابلة للتنفيذ. بدون تحيّز بشري، بدون تداول عاطفي — فقط وضوح خوارزمي صافٍ.",
        features: [
          "مسح آلي للأسواق على مدار الساعة مع التعرّف على الأنماط",
          "كشف الإشارات اللحظية عبر فئات أصول متعددة",
          "تقارير تحليلية يومية وأسبوعية مولّدة بالذكاء الاصطناعي",
          "تنبيهات مخصصة تصلك عبر تيليغرام أو البريد أو واتساب",
          "مطابقة الأنماط التاريخية مع نماذج تنبؤية"
        ],
        example: "كل صباح الساعة السادسة، تصلك نشرة سوقية شاملة — اتجاهات، شذوذات، وفرص — حُلّلت وكُتبت بالكامل بالذكاء الاصطناعي."
      },
      {
        title: "الإنتاج بالذكاء الاصطناعي",
        subtitle: "من السيناريو إلى الشاشة، آليًا بالكامل",
        desc: "إنتاج سينمائي متكامل يعمل بالذكاء الاصطناعي بالكامل. أنظمتنا تتولّى كل مرحلة — الكتابة، التصميم البصري، التعليق الصوتي، المونتاج، والتسليم النهائي — بدون أي تدخل بشري.",
        features: [
          "سيناريوهات ولوحات قصصية مولّدة بالذكاء الاصطناعي",
          "إنتاج بصري آلي باستخدام أحدث النماذج",
          "تعليق صوتي طبيعي بعدة لغات",
          "مونتاج ذكي مع اختيار الإيقاع والموسيقى",
          "تسليم نهائي بجودة بث احترافية"
        ],
        example: "تصف رؤيتك في جملة واحدة. خلال ساعات، تستلم فيديو كامل الإنتاج — مكتوب ومعلّق عليه ومُنتَج وجاهز للنشر."
      },
      {
        title: "أتمتة العمليات",
        subtitle: "أنظمة لا تنام",
        desc: "نبني خطوط أتمتة ذكية تربط أدواتك وتزيل المهام المتكررة وتُبقي عملياتك تعمل على مدار الساعة. كل مسار عمل مصمّم ليعمل باستقلالية تامة — بدون مراقبة.",
        features: [
          "ردود آلية ذكية على البريد الإلكتروني وتوجيهه",
          "خطوط جمع البيانات ومعالجتها وإعداد التقارير",
          "تكامل عبر المنصات (إدارة علاقات العملاء، البريد، السوشال، قواعد البيانات)",
          "تنفيذ مهام مجدولة مع معالجة الأخطاء",
          "أنظمة مراقبة ذاتية مع تعافٍ تلقائي"
        ],
        example: "عميل يرسل استفسارًا الساعة الثالثة فجرًا. النظام يقرأه، يصنّفه، يكتب ردًا مخصصًا، ويجدول متابعة — كل ذلك قبل طلوع الشمس."
      },
      {
        title: "محرك المحتوى",
        subtitle: "صوت علامتك التجارية، مضخّمًا بالذكاء الاصطناعي",
        desc: "نظام إنتاج محتوى مستقل بالكامل. الذكاء الاصطناعي يكتب ويصمّم ويجدول وينشر المحتوى على جميع منصاتك — يحافظ على صوت علامتك التجارية أثناء العمل بشكل آلي تمامًا.",
        features: [
          "ثريدات ومنشورات ومقالات ونشرات بريدية مكتوبة بالذكاء الاصطناعي",
          "جدولة ونشر تلقائي على جميع المنصات",
          "تدريب على صوت العلامة — الذكاء الاصطناعي يتعلّم أسلوبك ونبرتك",
          "تحليل الأداء مع تحسين المحتوى تلقائيًا",
          "إنتاج محتوى متعدد اللغات (عربي وإنجليزي)"
        ],
        example: "حسابك على X ينشر ثريدًا يوميًا الساعة التاسعة صباحًا، إنستغرام يحصل على carousel عند الظهر، ونشرتك البريدية تصدر كل خميس — كل ذلك بدون أن تلمس لوحة المفاتيح."
      },
      {
        title: "وكلاء أذكياء",
        subtitle: "موظفون رقميون يعملون على مدار الساعة",
        desc: "وكلاء مستقلون مصمّمون خصيصًا للتعامل مع مهام الأعمال الحقيقية. من دعم العملاء إلى تحليل البيانات واتخاذ القرارات — يعملون باستقلالية، يتعلّمون من التفاعلات، ويتحسّنون مع الوقت.",
        features: [
          "دعم عملاء ذكي عبر المحادثة والبريد والصوت",
          "تحليل بيانات مستقل وإنشاء التقارير",
          "مراقبة فورية للأنظمة والأسواق والمنافسين",
          "تفويض المهام وتنسيق مسارات العمل",
          "تعلّم مستمر وتحسّن ذاتي"
        ],
        example: "وكيلك الذكي يتعامل مع 200 استفسار عميل يوميًا، يصعّد المسائل المعقدة، يحدّث نظام إدارة العملاء، ويرسل لك ملخصًا كل مساء — بشكل مستقل تمامًا."
      },
      {
        title: "أكاديمية خوارزم",
        subtitle: "من الصفر إلى الأتمتة الكاملة",
        desc: "برامج تعليمية شاملة مصمّمة لتعلّمك كيف تبني وتُطلق وتحقق أرباحًا من أنظمة الذكاء الاصطناعي. سواء كنت مبتدئًا أو مطورًا خبيرًا، منهجنا يأخذك من فهم الذكاء الاصطناعي إلى الربح منه.",
        features: [
          "دورات منهجية من المبتدئ إلى المتقدم",
          "مشاريع تطبيقية لبناء وكلاء ذكاء اصطناعي حقيقيين",
          "ورش عمل مباشرة ودروس مسجّلة",
          "مجتمع تعليمي مع دعم وتعلّم بين الأقران",
          "شهادة إتمام عند إكمال الدورة"
        ],
        example: "خلال 8 أسابيع، تنتقل من عدم معرفة أي شيء عن الذكاء الاصطناعي إلى إطلاق أول وكيل مستقل يحقق إيرادات وأنت نائم."
      }
    ]
  };
  const ff = r ? "'Amiri', serif" : "'Cinzel', Georgia, serif";
  const fb = r ? "'Amiri', serif" : "'Cormorant Garamond', Georgia, serif";
  const g = "#C8963E";
  const oxs = light ? "#352222" : "#2A1414";
  const bk = light ? "#3D3947" : "#131316";
  const bk2 = light ? "#4A4558" : "#1B191F";
  const ln = light ? "#FAF7F2" : "#F0EAE0";
  const m = light ? "#C8B8A8" : "#A89B85";
  const gs = "rgba(200,150,62,0.65)"; /* gold soft */
  const gb = "rgba(200,150,62,0.12)"; /* Subtle gold border — much lighter */

  /* RTL-aware helpers: zero out letter-spacing and uppercase for Arabic */
  const ls = (v) => r ? 0 : v;
  const uc = r ? "none" : "uppercase";

  useEffect(() => {
    setOn(true);
    
    // Logo assembly sequence: equation → dissolve → logo → settled
    const t1 = setTimeout(() => setLogoPhase(1), 2200);
    const t2 = setTimeout(() => setLogoPhase(2), 3500);
    const t3 = setTimeout(() => setLogoPhase(3), 5500);
    
    // Algorithm Visualizer — types code line by line
    const codeScript = [
      "# Al-Khwarizmi's Method",
      "# The origin of algebra, c. 820",
      "",
      "def solve(a, b, c):",
      "    \"\"\"Find balance in chaos\"\"\"",
      "    d = b**2 - 4*a*c",
      "    x1 = (-b + sqrt(d)) / (2*a)",
      "    x2 = (-b - sqrt(d)) / (2*a)",
      "    return x1, x2",
      "",
      "# Where algorithms meet ambition",
      "equation = solve(1, -5, 6)",
      "→ [2.0, 3.0]  ✓ balanced",
      "",
      "// 1200 years later...",
      "// the equation continues."
    ];
    
    let lineIdx = 0;
    let charIdx = 0;
    let currentLines = [];
    let typeInterval;
    let resetTimeout;
    
    const typeChar = () => {
      if (lineIdx >= codeScript.length) {
        // Finished — wait then restart
        resetTimeout = setTimeout(() => {
          lineIdx = 0;
          charIdx = 0;
          currentLines = [];
          setCodeLines([]);
          typeInterval = setInterval(typeChar, 45);
        }, 4000);
        clearInterval(typeInterval);
        return;
      }
      const currentLine = codeScript[lineIdx];
      if (charIdx < currentLine.length) {
        const partialLine = currentLine.substring(0, charIdx + 1);
        const newLines = [...currentLines, partialLine];
        setCodeLines(newLines);
        charIdx++;
      } else {
        currentLines = [...currentLines, currentLine];
        lineIdx++;
        charIdx = 0;
      }
    };
    
    typeInterval = setInterval(typeChar, 45);
    
    // Sacred geometry rotation
    const sacredInterval = setInterval(() => {
      setSacredAngle(prev => prev + 0.15);
    }, 50);
    
    // Cursor blink
    const cursorInterval = setInterval(() => {
      setCodeCursor(prev => !prev);
    }, 500);
    
    
    // Scroll Reveal Observer
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('in');
        }
      });
    }, { threshold: 0.15, rootMargin: '0px 0px -10% 0px' });
    
    setTimeout(() => {
      document.querySelectorAll('.reveal, .reveal-scale').forEach(el => observer.observe(el));
    }, 100);
    
    // Chaos to Order animation
    const canvas = document.getElementById('chaos-canvas');
    if (canvas) {
      const ctx = canvas.getContext('2d');
      const setSize = () => {
        canvas.width = canvas.offsetWidth;
        canvas.height = canvas.offsetHeight;
      };
      setSize();
      window.addEventListener('resize', setSize);
      
      const numParticles = 80;
      const particles = [];
      for (let i = 0; i < numParticles; i++) {
        const angle = (i / numParticles) * Math.PI * 2;
        const radius = 100 + Math.sin(i * 0.5) * 20;
        particles.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          tx: canvas.width / 2 + Math.cos(angle) * radius,
          ty: canvas.height / 2 + Math.sin(angle) * radius,
          vx: 0, vy: 0,
          size: 2 + Math.random() * 2,
          opacity: 0
        });
      }
      
      let frame = 0;
      let animId;
      const animate = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        frame++;
        
        particles.forEach((p, i) => {
          // Phase 1: chaos (frames 0-90)
          // Phase 2: gathering (frames 90-180)
          // Phase 3: settled (frames 180+)
          const dx = p.tx - p.x;
          const dy = p.ty - p.y;
          const force = frame > 90 ? 0.025 : 0.003;
          p.vx += dx * force;
          p.vy += dy * force;
          p.vx *= 0.93;
          p.vy *= 0.93;
          p.x += p.vx;
          p.y += p.vy;
          
          if (frame < 30) p.opacity = Math.min(p.opacity + 0.015, 0.85);
          else if (frame > 300) p.opacity = Math.max(p.opacity - 0.003, 0.25);
          
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(200, 150, 62, ${p.opacity})`;
          ctx.fill();
          
          // Draw connections when settled
          if (frame > 200) {
            for (let j = i + 1; j < particles.length; j++) {
              const p2 = particles[j];
              const dist = Math.hypot(p.x - p2.x, p.y - p2.y);
              if (dist < 70) {
                ctx.beginPath();
                ctx.moveTo(p.x, p.y);
                ctx.lineTo(p2.x, p2.y);
                ctx.strokeStyle = `rgba(200, 150, 62, ${0.08 * (1 - dist/70)})`;
                ctx.lineWidth = 0.5;
                ctx.stroke();
              }
            }
          }
        });
        
        if (frame === 180) 
        animId = requestAnimationFrame(animate);
      };
      animate();
      
      return () => {
        cancelAnimationFrame(animId);
        window.removeEventListener('resize', setSize);
      };
    }
    
    const h = () => {
      setSy(window.scrollY); setShowBtt(window.scrollY > 400);
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = scrollHeight > 0 ? (window.scrollY / scrollHeight) * 100 : 0;
      setScrollProgress(progress);
      for (const id of ["contact", "about", "portfolio", "services", "home"]) {
        const e = document.getElementById(id);
        if (e && e.getBoundingClientRect().top < 200) { setSec(id); break; }
      }
    };
    window.addEventListener("scroll", h);
    return () => window.removeEventListener("scroll", h);
  }, []);

  const go = id => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  const f = (d = 0) => ({
    opacity: on ? 1 : 0,
    transform: on ? "translateY(0)" : "translateY(30px)",
    transition: `all 0.9s cubic-bezier(0.16,1,0.3,1) ${d}s`
  });

  const SacredGeometry = () => (
    <div className="sacred" aria-hidden="true">
      <svg className="s1" style={{transform: `rotate(${sacredAngle}deg)`}} viewBox="0 0 200 200" fill="none" stroke="#C8963E" strokeWidth="0.4">
        <circle cx="100" cy="100" r="80" />
        <circle cx="100" cy="100" r="60" />
        <circle cx="100" cy="100" r="40" />
        {[...Array(12)].map((_, i) => {
          const a = (i * 30) * Math.PI / 180;
          return <line key={i} x1="100" y1="100" x2={100 + Math.cos(a) * 80} y2={100 + Math.sin(a) * 80} />;
        })}
        {[...Array(8)].map((_, i) => {
          const a = (i * 45) * Math.PI / 180;
          const x = 100 + Math.cos(a) * 50;
          const y = 100 + Math.sin(a) * 50;
          return <circle key={`c${i}`} cx={x} cy={y} r="20" />;
        })}
        <polygon points="100,30 130,85 130,115 100,170 70,115 70,85" />
      </svg>
      <svg className="s2" style={{transform: `rotate(${-sacredAngle * 0.8}deg)`}} viewBox="0 0 200 200" fill="none" stroke="#C8963E" strokeWidth="0.4">
        <circle cx="100" cy="100" r="80" />
        <polygon points="100,30 156,65 156,135 100,170 44,135 44,65" />
        <polygon points="100,170 44,135 44,65 100,30 156,65 156,135" transform="rotate(30 100 100)" />
        {[...Array(6)].map((_, i) => {
          const a = (i * 60) * Math.PI / 180;
          const x = 100 + Math.cos(a) * 40;
          const y = 100 + Math.sin(a) * 40;
          return <circle key={i} cx={x} cy={y} r="40" />;
        })}
      </svg>
    </div>
  );

  return (
    <div dir={r ? "rtl" : "ltr"} style={{ fontFamily: fb, background: bk, color: ln, minHeight: "100vh", overflowX: "hidden", position: "relative" }}>
      <SacredGeometry />
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@400;500;600;700&family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;1,400&family=Noto+Kufi+Arabic:wght@300;400;500;600;700&display=swap');
        * { margin: 0; padding: 0; box-sizing: border-box; } html { scroll-behavior: smooth; } .btt { position: fixed; bottom: 32px; right: 32px; width: 44px; height: 44px; background: rgba(200,150,62,0.15); border: 1px solid rgba(200,150,62,0.4); border-radius: 50%; display: flex; align-items: center; justify-content: center; cursor: pointer; z-index: 999; transition: all 0.3s; } .btt:hover { background: rgba(200,150,62,0.3); transform: translateY(-3px); }
        html { scroll-behavior: smooth; }
        ::selection { background: ${g}; color: ${bk}; }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes float { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-12px); } }
        @keyframes drift1 {
          0% { transform: translate(-180px, -120px) rotate(-45deg); opacity: 0.3; }
          100% { transform: translate(0, 0) rotate(0deg); opacity: 1; }
        }
        @keyframes drift2 {
          0% { transform: translate(200px, -150px) rotate(60deg); opacity: 0.3; }
          100% { transform: translate(0, 0) rotate(0deg); opacity: 1; }
        }
        @keyframes drift3 {
          0% { transform: translate(-220px, 180px) rotate(120deg); opacity: 0.3; }
          100% { transform: translate(0, 0) rotate(0deg); opacity: 1; }
        }
        @keyframes drift4 {
          0% { transform: translate(180px, 200px) rotate(-90deg); opacity: 0.3; }
          100% { transform: translate(0, 0) rotate(0deg); opacity: 1; }
        }
        @keyframes driftCenter {
          0% { transform: translate(0, -250px) scale(0.5); opacity: 0; }
          100% { transform: translate(0, 0) scale(1); opacity: 1; }
        }
        @keyframes equationFade {
          0% { opacity: 0; transform: translateY(20px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        
        .eq-text {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          font-family: 'Cormorant Garamond', Georgia, serif;
          font-size: 42px;
          font-style: italic;
          color: #C8963E;
          white-space: nowrap;
          pointer-events: none;
          z-index: 3;
          letter-spacing: 4px;
          text-shadow: 0 0 30px rgba(200,150,62,0.3);
        }
        .eq-text.dissolve {
          animation: eqDissolve 1.3s cubic-bezier(0.4, 0, 1, 1) forwards;
        }
        @keyframes eqDissolve {
          0% { opacity: 1; transform: translate(-50%, -50%) scale(1); filter: blur(0); }
          50% { opacity: 0.6; transform: translate(-50%, -50%) scale(1.15); filter: blur(4px); letter-spacing: 20px; }
          100% { opacity: 0; transform: translate(-50%, -50%) scale(1.5); filter: blur(16px); letter-spacing: 40px; }
        }
        @keyframes eqFadeIn {
          0% { opacity: 0; transform: translate(-50%, -50%) scale(0.8); filter: blur(8px); }
          100% { opacity: 1; transform: translate(-50%, -50%) scale(1); filter: blur(0); }
        }
        @keyframes particleGather {
          0% { opacity: 0.8; }
          50% { opacity: 1; }
          100% { opacity: 0; }
        }
        .gather-particles {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 400px;
          height: 400px;
          pointer-events: none;
          z-index: 2;
        }
        .g-particle {
          position: absolute;
          width: 3px;
          height: 3px;
          background: #C8963E;
          border-radius: 50%;
          animation: particleGather 1.8s ease-in forwards;
        }
        .code-viz {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          font-family: 'SF Mono', Monaco, 'Courier New', monospace;
          font-size: 11px;
          line-height: 1.9;
          color: rgba(200,150,62,0.28);
          pointer-events: none;
          z-index: 1;
          max-width: 280px;
          white-space: pre;
          letter-spacing: 0;
        }
        [dir="ltr"] 
        .eq-text {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          font-family: 'Cormorant Garamond', Georgia, serif;
          font-size: 42px;
          font-style: italic;
          color: #C8963E;
          white-space: nowrap;
          pointer-events: none;
          z-index: 3;
          letter-spacing: 4px;
          text-shadow: 0 0 30px rgba(200,150,62,0.3);
        }
        .eq-text.dissolve {
          animation: eqDissolve 1.3s cubic-bezier(0.4, 0, 1, 1) forwards;
        }
        @keyframes eqDissolve {
          0% { opacity: 1; transform: translate(-50%, -50%) scale(1); filter: blur(0); }
          50% { opacity: 0.6; transform: translate(-50%, -50%) scale(1.15); filter: blur(4px); letter-spacing: 20px; }
          100% { opacity: 0; transform: translate(-50%, -50%) scale(1.5); filter: blur(16px); letter-spacing: 40px; }
        }
        @keyframes eqFadeIn {
          0% { opacity: 0; transform: translate(-50%, -50%) scale(0.8); filter: blur(8px); }
          100% { opacity: 1; transform: translate(-50%, -50%) scale(1); filter: blur(0); }
        }
        @keyframes particleGather {
          0% { opacity: 0.8; }
          50% { opacity: 1; }
          100% { opacity: 0; }
        }
        .gather-particles {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 400px;
          height: 400px;
          pointer-events: none;
          z-index: 2;
        }
        .g-particle {
          position: absolute;
          width: 3px;
          height: 3px;
          background: #C8963E;
          border-radius: 50%;
          animation: particleGather 1.8s ease-in forwards;
        }
        .code-viz { right: 4%; text-align: left; }
        [dir="rtl"] 
        .eq-text {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          font-family: 'Cormorant Garamond', Georgia, serif;
          font-size: 42px;
          font-style: italic;
          color: #C8963E;
          white-space: nowrap;
          pointer-events: none;
          z-index: 3;
          letter-spacing: 4px;
          text-shadow: 0 0 30px rgba(200,150,62,0.3);
        }
        .eq-text.dissolve {
          animation: eqDissolve 1.3s cubic-bezier(0.4, 0, 1, 1) forwards;
        }
        @keyframes eqDissolve {
          0% { opacity: 1; transform: translate(-50%, -50%) scale(1); filter: blur(0); }
          50% { opacity: 0.6; transform: translate(-50%, -50%) scale(1.15); filter: blur(4px); letter-spacing: 20px; }
          100% { opacity: 0; transform: translate(-50%, -50%) scale(1.5); filter: blur(16px); letter-spacing: 40px; }
        }
        @keyframes eqFadeIn {
          0% { opacity: 0; transform: translate(-50%, -50%) scale(0.8); filter: blur(8px); }
          100% { opacity: 1; transform: translate(-50%, -50%) scale(1); filter: blur(0); }
        }
        @keyframes particleGather {
          0% { opacity: 0.8; }
          50% { opacity: 1; }
          100% { opacity: 0; }
        }
        .gather-particles {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 400px;
          height: 400px;
          pointer-events: none;
          z-index: 2;
        }
        .g-particle {
          position: absolute;
          width: 3px;
          height: 3px;
          background: #C8963E;
          border-radius: 50%;
          animation: particleGather 1.8s ease-in forwards;
        }
        .code-viz { left: 4%; text-align: left; direction: ltr; }
        .code-viz .line { display: block; min-height: 1.9em; }
        .code-viz .comment { color: rgba(168,155,133,0.35); font-style: italic; }
        .code-viz .keyword { color: rgba(200,150,62,0.45); }
        .code-cursor {
          display: inline-block;
          width: 7px;
          height: 12px;
          background: rgba(200,150,62,0.6);
          margin-left: 2px;
          vertical-align: middle;
        }
        @media (max-width: 900px) {
          
        .eq-text {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          font-family: 'Cormorant Garamond', Georgia, serif;
          font-size: 42px;
          font-style: italic;
          color: #C8963E;
          white-space: nowrap;
          pointer-events: none;
          z-index: 3;
          letter-spacing: 4px;
          text-shadow: 0 0 30px rgba(200,150,62,0.3);
        }
        .eq-text.dissolve {
          animation: eqDissolve 1.3s cubic-bezier(0.4, 0, 1, 1) forwards;
        }
        @keyframes eqDissolve {
          0% { opacity: 1; transform: translate(-50%, -50%) scale(1); filter: blur(0); }
          50% { opacity: 0.6; transform: translate(-50%, -50%) scale(1.15); filter: blur(4px); letter-spacing: 20px; }
          100% { opacity: 0; transform: translate(-50%, -50%) scale(1.5); filter: blur(16px); letter-spacing: 40px; }
        }
        @keyframes eqFadeIn {
          0% { opacity: 0; transform: translate(-50%, -50%) scale(0.8); filter: blur(8px); }
          100% { opacity: 1; transform: translate(-50%, -50%) scale(1); filter: blur(0); }
        }
        @keyframes particleGather {
          0% { opacity: 0.8; }
          50% { opacity: 1; }
          100% { opacity: 0; }
        }
        .gather-particles {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 400px;
          height: 400px;
          pointer-events: none;
          z-index: 2;
        }
        .g-particle {
          position: absolute;
          width: 3px;
          height: 3px;
          background: #C8963E;
          border-radius: 50%;
          animation: particleGather 1.8s ease-in forwards;
        }
        .code-viz {
            font-size: 9px;
            line-height: 1.7;
            max-width: 160px;
            opacity: 0.6;
            top: auto;
            bottom: 8%;
            transform: none;
          }
          [dir="ltr"] 
        .eq-text {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          font-family: 'Cormorant Garamond', Georgia, serif;
          font-size: 42px;
          font-style: italic;
          color: #C8963E;
          white-space: nowrap;
          pointer-events: none;
          z-index: 3;
          letter-spacing: 4px;
          text-shadow: 0 0 30px rgba(200,150,62,0.3);
        }
        .eq-text.dissolve {
          animation: eqDissolve 1.3s cubic-bezier(0.4, 0, 1, 1) forwards;
        }
        @keyframes eqDissolve {
          0% { opacity: 1; transform: translate(-50%, -50%) scale(1); filter: blur(0); }
          50% { opacity: 0.6; transform: translate(-50%, -50%) scale(1.15); filter: blur(4px); letter-spacing: 20px; }
          100% { opacity: 0; transform: translate(-50%, -50%) scale(1.5); filter: blur(16px); letter-spacing: 40px; }
        }
        @keyframes eqFadeIn {
          0% { opacity: 0; transform: translate(-50%, -50%) scale(0.8); filter: blur(8px); }
          100% { opacity: 1; transform: translate(-50%, -50%) scale(1); filter: blur(0); }
        }
        @keyframes particleGather {
          0% { opacity: 0.8; }
          50% { opacity: 1; }
          100% { opacity: 0; }
        }
        .gather-particles {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 400px;
          height: 400px;
          pointer-events: none;
          z-index: 2;
        }
        .g-particle {
          position: absolute;
          width: 3px;
          height: 3px;
          background: #C8963E;
          border-radius: 50%;
          animation: particleGather 1.8s ease-in forwards;
        }
        .code-viz { right: 4%; }
          [dir="rtl"] 
        .eq-text {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          font-family: 'Cormorant Garamond', Georgia, serif;
          font-size: 42px;
          font-style: italic;
          color: #C8963E;
          white-space: nowrap;
          pointer-events: none;
          z-index: 3;
          letter-spacing: 4px;
          text-shadow: 0 0 30px rgba(200,150,62,0.3);
        }
        .eq-text.dissolve {
          animation: eqDissolve 1.3s cubic-bezier(0.4, 0, 1, 1) forwards;
        }
        @keyframes eqDissolve {
          0% { opacity: 1; transform: translate(-50%, -50%) scale(1); filter: blur(0); }
          50% { opacity: 0.6; transform: translate(-50%, -50%) scale(1.15); filter: blur(4px); letter-spacing: 20px; }
          100% { opacity: 0; transform: translate(-50%, -50%) scale(1.5); filter: blur(16px); letter-spacing: 40px; }
        }
        @keyframes eqFadeIn {
          0% { opacity: 0; transform: translate(-50%, -50%) scale(0.8); filter: blur(8px); }
          100% { opacity: 1; transform: translate(-50%, -50%) scale(1); filter: blur(0); }
        }
        @keyframes particleGather {
          0% { opacity: 0.8; }
          50% { opacity: 1; }
          100% { opacity: 0; }
        }
        .gather-particles {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 400px;
          height: 400px;
          pointer-events: none;
          z-index: 2;
        }
        .g-particle {
          position: absolute;
          width: 3px;
          height: 3px;
          background: #C8963E;
          border-radius: 50%;
          animation: particleGather 1.8s ease-in forwards;
        }
        .code-viz { left: 4%; }
        }
        @keyframes equationPulse {
          0%, 100% { opacity: 0.7; }
          50% { opacity: 1; }
        }
        .logo-piece {
          transform-origin: 250px 250px;
          transition: transform 2s cubic-bezier(0.16, 1, 0.3, 1), opacity 1.5s ease-out;
        }
        .logo-piece.chaos {
          opacity: 0.6;
        }
        .logo-piece.p1.chaos { transform: translate(-180px, -120px) rotate(-45deg); }
        .logo-piece.p2.chaos { transform: translate(200px, -150px) rotate(60deg); }
        .logo-piece.p3.chaos { transform: translate(-220px, 180px) rotate(120deg); }
        .logo-piece.p4.chaos { transform: translate(180px, 200px) rotate(-90deg); }
        .logo-piece.center.chaos { transform: translate(0, -250px) scale(0.5); opacity: 0; }
        .equation-text {
          opacity: 0;
          transition: opacity 1s ease-out 0.5s;
          font-family: 'Cinzel', serif;
          letter-spacing: 6px;
          font-size: 14px;
          color: #C8963E;
        }
        .equation-text.show { opacity: 1; }
        @keyframes sacredRotate { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes sacredFade { 0%,100% { opacity: 0.025; } 50% { opacity: 0.05; } }
        @keyframes revealUp {
          from { opacity: 0; transform: translateY(40px); filter: blur(8px); }
          to { opacity: 1; transform: translateY(0); filter: blur(0); }
        }
        @keyframes revealScale {
          from { opacity: 0; transform: scale(0.92); filter: blur(6px); }
          to { opacity: 1; transform: scale(1); filter: blur(0); }
        }
        .reveal {
          opacity: 0;
          transform: translateY(40px);
          filter: blur(8px);
          transition: opacity 1.2s cubic-bezier(0.16, 1, 0.3, 1), transform 1.2s cubic-bezier(0.16, 1, 0.3, 1), filter 1.2s ease-out;
        }
        .reveal.in {
          opacity: 1;
          transform: translateY(0);
          filter: blur(0);
        }
        .reveal-scale {
          opacity: 0;
          transform: scale(0.94);
          filter: blur(6px);
          transition: opacity 1s cubic-bezier(0.16, 1, 0.3, 1), transform 1s cubic-bezier(0.16, 1, 0.3, 1), filter 1s ease-out;
        }
        .reveal-scale.in {
          opacity: 1;
          transform: scale(1);
          filter: blur(0);
        }
        .sacred {
          position: fixed;
          inset: 0;
          pointer-events: none;
          z-index: 0;
          overflow: hidden;
          opacity: 0.06;
        }
        
        .sacred svg { opacity: 0.7;
          position: absolute;
          width: 800px;
          height: 800px;
          
        }
        .sacred svg.s1 {
          top: -200px;
          left: -200px;
        }
        .sacred svg.s2 {
          bottom: -200px;
          right: -200px;
          
        }
        @keyframes dl { from { width: 0; } to { width: 80px; } }

        .nl {
          color: ${m}; text-decoration: none; font-size: 13px; letter-spacing: 3px;
          text-transform: uppercase; transition: color .3s; cursor: pointer;
          font-family: 'Cinzel', serif;
        }
        .nl:hover, .nl.a { color: ${g}; }

        .sc {
          background: rgba(42,20,20,.4); border: .5px solid ${gb};
          padding: 44px 32px; transition: all .5s; cursor: default;
          position: relative; overflow: hidden;
        }
        .sc:hover { border-color: ${g}; transform: translateY(-6px); background: rgba(42,20,20,.6); }
        .sc::after {
          content: ''; position: absolute; top: 0; left: 0; right: 0;
          height: 0; background: ${g}; transition: height .4s;
        }
        .sc:hover::after { height: 2px; }

        .pc {
          background: rgba(42,20,20,.5); border: .5px solid ${gb};
          overflow: hidden; transition: all .5s; cursor: default;
        }
        .pc:hover { border-color: ${g}; transform: scale(1.03); }

        .bt {
          background: transparent; color: ${g}; border: 1px solid ${g};
          padding: 18px 56px; font-family: 'Cinzel', serif; font-size: 14px;
          letter-spacing: 4px; text-transform: uppercase; cursor: pointer;
          transition: all .4s; font-weight: 500;
        }
        .bt:hover { background: ${g}; color: ${bk}; }

        .fi {
          background: transparent; border: none; border-bottom: .5px solid ${gb};
          color: ${ln}; padding: 18px 0; font-family: 'Cormorant Garamond', serif;
          font-size: 17px; width: 100%; outline: none; transition: border-color .3s;
        }
        .fi:focus { border-color: ${g}; }
        .fi::placeholder { color: rgba(200,150,62,.3); }

        .lb {
          background: transparent; border: 1px solid ${gb}; color: ${g};
          padding: 7px 18px; cursor: pointer; font-size: 12px; letter-spacing: 2px;
          transition: all .3s; font-family: 'Cinzel', serif;
        }
        .lb:hover { background: rgba(200,150,62,.08); }

        .gl { height: 1px; background: linear-gradient(90deg, transparent, ${g}, transparent); }
        .sp { padding: 120px 24px; max-width: 1200px; margin: 0 auto; }

        /* ===== RTL / Arabic overrides ===== */
        [dir="rtl"] .nl,
        [dir="rtl"] .bt,
        [dir="rtl"] label,
        [dir="rtl"] .lb {
          letter-spacing: 0 !important;
          text-transform: none !important;
          font-family: 'Amiri', serif !important;
        }
        [dir="rtl"] .fi {
          font-family: 'Amiri', serif !important;
          text-align: right;
        }
        [dir="rtl"] .fi::placeholder {
          text-align: right;
        }

        /* Mobile responsive */
        @media (max-width: 768px) {
          .sp { padding: 80px 16px; }
          .desk-nav { display: none !important; }
          .mob-btn { display: flex !important; }
        }
        @media (min-width: 769px) {
          .mob-btn { display: none !important; }
          .mob-menu { display: none !important; }
        }
      `}</style>

      {/* ═══════════════ NAVBAR ═══════════════ */}
      <nav style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000,
        background: sy > 60 ? "rgba(14,14,16,.96)" : "transparent",
        backdropFilter: sy > 60 ? "blur(24px)" : "none",
        borderBottom: sy > 60 ? `.5px solid ${gb}` : "none",
        transition: "all .5s", padding: "0 24px"
      }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", height: 76 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14, cursor: "pointer", flexShrink: 0 }} onClick={() => go("home")}>
            <Logo size={34} />
            <span style={{ fontSize: 18, fontWeight: 600, color: g, letterSpacing: 6, textTransform: "uppercase", fontFamily: "'Cinzel', serif", whiteSpace: "nowrap" }}>
              Khwarizm
            </span>
          </div>
          {/* Desktop nav */}
          <div className="desk-nav" style={{ display: "flex", alignItems: "center", gap: r ? 20 : 28, marginLeft: r ? 0 : 40, marginRight: r ? 40 : 0 }}>
            {Object.entries(c.nav).map(([k, v]) => (
              <span key={k} className={`nl ${sec === k ? "a" : ""}`} onClick={() => go(k)}>{v}</span>
            ))}
            <button className="lb" onClick={() => setLang(lang === "en" ? "ar" : "en")}>
              {lang === "en" ? "عربي" : "EN"}
            </button>
            <button className="lb" onClick={() => setLight(!light)} style={{marginLeft: 8}}>
              {light ? "🌙" : "☀️"}
            </button>
          </div>
          {/* Mobile hamburger */}
          <div className="mob-btn" style={{ display: "none", cursor: "pointer", padding: 8 }} onClick={() => setMob(!mob)}>
            <div style={{ width: 24, display: "flex", flexDirection: "column", gap: 5 }}>
              <div style={{ height: 2, background: g, transition: "all .3s", transform: mob ? "rotate(45deg) translateY(7px)" : "none" }} />
              <div style={{ height: 2, background: g, transition: "all .3s", opacity: mob ? 0 : 1 }} />
              <div style={{ height: 2, background: g, transition: "all .3s", transform: mob ? "rotate(-45deg) translateY(-7px)" : "none" }} />
            </div>
          </div>
        </div>
      </nav>
      {/* Mobile menu overlay */}
      {mob && (
        <div className="mob-menu" style={{
          position: "fixed", top: 76, left: 0, right: 0, bottom: 0, zIndex: 999,
          background: "rgba(14,14,16,.98)", backdropFilter: "blur(24px)",
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 32
        }}>
          {Object.entries(c.nav).map(([k, v]) => (
            <span key={k} className={`nl ${sec === k ? "a" : ""}`} style={{ fontSize: 18 }}
              onClick={() => { go(k); setMob(false); }}>{v}</span>
          ))}
          <button className="lb" style={{ marginTop: 16, padding: "12px 32px" }}
            onClick={() => { setLang(lang === "en" ? "ar" : "en"); setMob(false); }}>
            {lang === "en" ? "عربي" : "EN"}
          </button>
          <button className="lb" style={{marginTop: 12}}
            onClick={() => { setLight(!light); setMob(false); }}>
            {light ? "🌙 Dark" : "☀️ Light"}
          </button>
        </div>
      )}
      <KhwarizmChat isRTL={r} />

      {/* ═══════════════ HERO ═══════════════ */}
      <section id="home" style={{
        minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
        position: "relative", overflow: "hidden", textAlign: "center",
        background: `radial-gradient(ellipse at center, ${oxs} 0%, ${bk} 60%)`
      }}>
        
        
        {/* Equation → Logo Sequence */}
        {logoPhase <= 1 && (
          <div className={`eq-text ${logoPhase === 1 ? 'dissolve' : ''}`}
               style={logoPhase === 0 ? {animation: 'eqFadeIn 1.5s ease-out forwards'} : {}}>
            x² + 10x = 39
          </div>
        )}
      <KhwarizmChat isRTL={r} />
        {logoPhase >= 1 && logoPhase <= 2 && (
          <div className="gather-particles">
            {[...Array(40)].map((_, i) => {
              const angle = (i / 40) * Math.PI * 2;
              const radius = 120 + Math.random() * 80;
              const startX = Math.cos(angle) * radius + 200;
              const startY = Math.sin(angle) * radius + 200;
              const delay = Math.random() * 0.8;
              return (
                <div key={i} className="g-particle" style={{
                  left: startX,
                  top: startY,
                  animationDelay: `${delay}s`,
                  width: 2 + Math.random() * 3,
                  height: 2 + Math.random() * 3,
                  boxShadow: '0 0 6px rgba(200,150,62,0.5)',
                  transform: `translate(${(200 - startX) * 0.7}px, ${(200 - startY) * 0.7}px)`,
                  transition: `transform 1.8s cubic-bezier(0.16, 1, 0.3, 1) ${delay}s`
                }} />
              );
            })}
          </div>
        )}
      <KhwarizmChat isRTL={r} />
        <div style={{
          position: "absolute", top: "50%", left: "50%",
          width: 380, height: 380,
          transform: "translate(-50%, -50%)",
          pointerEvents: "none", zIndex: 1,
          opacity: logoPhase >= 2 ? (logoPhase === 3 ? 0.10 : 0.5) : 0,
          filter: logoPhase === 2 ? "blur(2px)" : "blur(0)",
          transition: "opacity 2s ease-out, filter 2s ease-out"
        }}>
          <img src={LOGO_SRC} alt="" style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }} />
        </div>
        
        {/* Algorithm Visualizer */}
        <div className="code-viz" aria-hidden="true">
          {codeLines.map((line, i) => {
            const isComment = line.trim().startsWith("#") || line.trim().startsWith("//");
            const isLast = i === codeLines.length - 1;
            return (
              <span key={i} className={`line ${isComment ? 'comment' : ''}`}>
                {line || "\u00A0"}
                {isLast && codeCursor && <span className="code-cursor" />}
              </span>
            );
          })}
        </div>
        <div style={{ position: "absolute", inset: 0, opacity: .02, pointerEvents: "none", backgroundImage: `repeating-conic-gradient(${g} 0% 25%, transparent 0% 50%)`, backgroundSize: "80px 80px" }} />
        <div style={{ position: "relative", zIndex: 1, padding: "0 32px", maxWidth: 800, opacity: logoPhase >= 3 ? 1 : 0, transform: logoPhase >= 3 ? "translateY(0)" : "translateY(20px)", transition: "opacity 1.5s ease-out, transform 1.5s ease-out" }}>
          <div style={{ width: 80, height: 1, background: g, margin: "0 auto 40px", animation: "dl 2s ease-out" }} />
          <p style={{ ...f(.1), fontSize: r ? 16 : 12, letterSpacing: ls(6), textTransform: uc, color: g, marginBottom: 10, fontWeight: r ? 700 : 400, fontFamily: ff, transition: "opacity 0.8s ease-out" }}>
            {logoPhase >= 2 ? (r ? "الجبر ⟵ المقابلة" : "CHAOS ⟶ ORDER") : c.hero.tagline}
          </p>
          <p style={{ ...f(.2), fontSize: r ? 15 : 14, letterSpacing: ls(4), color: ln, marginBottom: 20, opacity: 0.75, fontFamily: ff }}>
            {c.hero.subtitle}
          </p>
          <h1 style={{ ...f(.4), fontSize: r ? "clamp(20px,2.2vw,30px)" : "clamp(22px,2.8vw,36px)", fontWeight: 300, lineHeight: r ? 1.6 : 1.2, color: ln, marginBottom: 20, whiteSpace: "pre-line", fontFamily: ff }}>
            {c.hero.title}
          </h1>
          <p style={{ ...f(.5), fontSize: 15, letterSpacing: ls(5), color: m, marginBottom: 40, fontFamily: fb }}>
            {c.hero.desc}
          </p>
          <p style={{ ...f(.6), fontSize: 15, fontStyle: "italic", color: "rgba(200,150,62,.5)", marginBottom: 48, fontFamily: fb, lineHeight: 1.8 }}>
            {c.hero.quote}
          </p>
          <div style={f(.7)}>
            <button className="bt" onClick={() => go("services")}>{c.hero.cta}</button>
          </div>
        </div>
        <div style={{ position: "absolute", bottom: 48, left: "50%", transform: "translateX(-50%)" }}>
          <div style={{ width: 1, height: 48, background: `linear-gradient(to bottom, ${g}, transparent)` }} />
        </div>
      </section>

      {/* ═══════════════ SERVICES ═══════════════ */}
      <section id="services" style={{ background: bk2 }}>
        <div className="sp">
          <div className="reveal" style={{ textAlign: "center", marginBottom: 80 }}>
            <p style={{ fontSize: r ? 14 : 12, letterSpacing: ls(8), textTransform: uc, color: gs, marginBottom: 16, fontFamily: ff }}>
              {c.services.subtitle}
            </p>
            <h2 style={{ fontSize: "clamp(30px,4vw,50px)", fontWeight: 300, color: ln, fontFamily: ff }}>
              {c.services.title}
            </h2>
            <div className="gl" style={{ width: 80, margin: "28px auto 0" }} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 20 }}>
            {c.services.items.map((s, i) => (
              <div key={i} className="sc reveal-scale" onClick={() => setSelectedService(i)} style={{cursor: "pointer"}}>
                <div style={{ fontSize: 28, marginBottom: 20 }} dangerouslySetInnerHTML={{__html: s.icon}} />
                <h3 style={{ fontSize: 20, fontWeight: 500, color: g, marginBottom: 14, fontFamily: ff, letterSpacing: ls(1) }}>
                  {s.title}
                </h3>
                <p style={{ fontSize: 15, color: m, lineHeight: 1.8, fontFamily: fb }}>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════ PROCESS ═══════════════ */}
      <section id="process">
        <div className="sp">
          <div className="reveal" style={{ textAlign: "center", marginBottom: 80 }}>
            <p style={{ fontSize: r ? 14 : 12, letterSpacing: ls(8), textTransform: uc, color: gs, marginBottom: 16, fontFamily: ff }}>
              {c.process.subtitle}
            </p>
            <h2 style={{ fontSize: "clamp(30px,4vw,50px)", fontWeight: 300, color: ln, fontFamily: ff }}>
              {c.process.title}
            </h2>
            <div className="gl" style={{ width: 80, margin: "28px auto 0" }} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 32 }}>
            {c.process.items.map((p, i) => (
              <div key={i} className="reveal-scale" style={{ textAlign: "center", padding: "24px 16px", position: "relative" }}>
                <div style={{ fontSize: 56, fontWeight: 200, color: g, fontFamily: ff, marginBottom: 16, opacity: 0.6, letterSpacing: ls(2) }}>
                  {p.num}
                </div>
                <div style={{ width: 40, height: 1, background: g, margin: "0 auto 24px", opacity: 0.4 }} />
                <h3 style={{ fontSize: 22, fontWeight: 500, color: ln, marginBottom: 16, fontFamily: ff, letterSpacing: ls(1) }}>
                  {p.title}
                </h3>
                <p style={{ fontSize: 14, color: m, lineHeight: 1.8, fontFamily: fb }}>
                  {p.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════ ABOUT ═══════════════ */}
      <section id="about" style={{ background: bk2 }}>
        <div style={{ maxWidth: 780, margin: "0 auto", padding: "120px 24px" }}>
          <div className="reveal" style={{ textAlign: "center", marginBottom: 64 }}>
            <h2 style={{ fontSize: "clamp(30px,4vw,50px)", fontWeight: 300, color: ln, fontFamily: ff }}>
              {c.about.title}
            </h2>
            <div className="gl" style={{ width: 80, margin: "28px auto 0" }} />
          </div>
          <div style={{ fontSize: 17, lineHeight: 2.1, color: m, textAlign: r ? "right" : "left", fontFamily: fb }}>
            <p style={{ marginBottom: 28 }}>{c.about.p1}</p>
            <p style={{ marginBottom: 28 }}>{c.about.p2}</p>
            <p style={{ marginBottom: 48 }}>{c.about.p3}</p>
          </div>
          <div style={{ textAlign: "center", padding: "56px 48px", borderTop: `.5px solid ${gb}`, borderBottom: `.5px solid ${gb}` }}>
            <div style={{ display: "flex", justifyContent: "center" }}><Logo size={100} /></div>
            <p style={{ fontSize: "clamp(22px,3vw,34px)", fontWeight: 300, fontStyle: "italic", color: g, lineHeight: 1.6, marginTop: 28, fontFamily: ff }}>
              "{c.about.quote}"
            </p>
          </div>
        </div>
      </section>

      {/* ═══════════════ CONTACT ═══════════════ */}
      <section id="contact">
        <div style={{ maxWidth: 560, margin: "0 auto", padding: "120px 24px" }}>
          <div className="reveal" style={{ textAlign: "center", marginBottom: 64 }}>
            <h2 style={{ fontSize: "clamp(30px,4vw,50px)", fontWeight: 300, color: ln, marginBottom: 16, fontFamily: ff }}>
              {c.contact.title}
            </h2>
            <p style={{ fontSize: 16, color: m, fontFamily: fb }}>{c.contact.subtitle}</p>
            <div className="gl" style={{ width: 80, margin: "28px auto 0" }} />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 36 }}>
            <div>
              <label style={{ fontSize: r ? 13 : 11, letterSpacing: ls(4), textTransform: uc, color: gs, fontFamily: ff }}>
                {c.contact.name}
              </label>
              <input id="cf-name" className="fi" placeholder="..." style={{ fontFamily: fb }} />
            </div>
            <div>
              <label style={{ fontSize: r ? 13 : 11, letterSpacing: ls(4), textTransform: uc, color: gs, fontFamily: ff }}>
                {c.contact.email}
              </label>
              <input id="cf-email" type="email" className="fi" placeholder="you@example.com" style={{ fontFamily: fb }} />
            </div>
            <div>
              <label style={{ fontSize: r ? 13 : 11, letterSpacing: ls(4), textTransform: uc, color: gs, fontFamily: ff }}>
                {c.contact.message}
              </label>
              <textarea id="cf-msg" className="fi" rows={4} placeholder="..." style={{ resize: "none", fontFamily: fb }} />
            </div>
            <div style={{ textAlign: "center", marginTop: 20 }}>
              <button className="bt" onClick={() => {
                const n = document.getElementById("cf-name")?.value || "";
                const e = document.getElementById("cf-email")?.value || "";
                const msg = document.getElementById("cf-msg")?.value || "";
                fetch("https://formspree.io/f/mlgzbqoz", {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ name: n, email: e, message: msg })
                }).then(r => {
                  if (r.ok) {
                    alert(lang === "ar" ? "تم إرسال رسالتك بنجاح!" : "Message sent successfully!");
                  } else {
                    alert(lang === "ar" ? "حدث خطأ، حاول مرة أخرى" : "Error, please try again");
                  }
                }).catch(() => alert(lang === "ar" ? "حدث خطأ، حاول مرة أخرى" : "Error, please try again"));
                }}>{c.contact.send}</button>
            </div>
          </div>
          <div style={{ textAlign: "center", marginTop: 72 }}>
            <p style={{ fontSize: r ? 13 : 11, letterSpacing: ls(5), textTransform: uc, color: "rgba(200,150,62,.4)", marginBottom: 24, fontFamily: ff }}>
              <a href="mailto:info@khwarizm.ai" style={{
                color: g, textDecoration: "none", fontSize: 15, fontFamily: fb,
                letterSpacing: ls(2), transition: "opacity 0.3s"
              }}
                onMouseEnter={e => e.target.style.opacity = "0.7"}
                onMouseLeave={e => e.target.style.opacity = "1"}
              >info@khwarizm.ai</a>
            </p>
            <p style={{ fontSize: r ? 13 : 12, letterSpacing: ls(6), textTransform: uc, color: gs, marginBottom: 28, fontFamily: ff }}>
              {c.contact.social}
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: r ? 16 : 28, rowGap: 16, padding: "0 16px" }}>
              {[{n:"X",u:"https://x.com/KhwarizmAii"},{n:"YouTube",u:"https://youtube.com/@KhwarizmAii"},{n:"Instagram",u:"https://instagram.com/KhwarizmAi"},{n:"TikTok",u:"https://tiktok.com/@khwarizm_a"},{n:"Telegram",u:"https://t.me/KhwarizmAii"},{n:"WhatsApp",u:"https://whatsapp.com/channel/0029Vb7uISNLY6cydVn8Cp0k"}].map(p => (
                <span key={p.n} style={{
                  fontSize: 13, color: "rgba(200,150,62,.35)", letterSpacing: 2,
                  cursor: "pointer", transition: "color .3s", fontFamily: "'Cinzel', serif"
                }}
                  onMouseEnter={e => e.target.style.color = g}
                  onMouseLeave={e => e.target.style.color = "rgba(200,150,62,.35)"} onClick={() => window.open(p.u, "_blank")}
                >
                  {p.n}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════ FOOTER ═══════════════ */}
      <footer style={{ borderTop: `.5px solid ${gb}`, padding: "48px 24px", textAlign: "center" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 14, marginBottom: 20 }}>
            <Logo size={28} />
            <span style={{ fontSize: 15, fontWeight: 600, color: g, letterSpacing: 5, textTransform: "uppercase", fontFamily: "'Cinzel', serif" }}>
              Khwarizm
            </span>
          </div>
          <p style={{ fontSize: 14, fontStyle: "italic", color: g, marginBottom: 8, fontFamily: fb }}>
            {c.footer.tagline}
          </p>
          <p style={{ fontSize: 11, color: "rgba(200,150,62,.4)", letterSpacing: ls(4), marginBottom: 6, fontFamily: ff }}>
            {c.footer.est}
          </p>
          <p style={{ fontSize: 10, color: m, opacity: 0.5, marginBottom: 10, fontFamily: fb, letterSpacing: ls(1), lineHeight: 1.6 }}>
            {c.footer.disclaimer}
          </p>
          <p style={{ fontSize: 11, color: "rgba(200,150,62,.2)", letterSpacing: ls(3), fontFamily: ff }}>
            © 2026 Khwarizm. {c.footer.rights}
          </p>
        </div>
      </footer>
      {/* Reading Progress Bar */}
      <div style={{
        position: "fixed", top: 0, left: 0, height: 2,
        width: `${scrollProgress}%`,
        background: "linear-gradient(90deg, rgba(200,150,62,0.3), #C8963E)",
        zIndex: 9999, transition: "width 0.1s ease-out",
        boxShadow: "0 0 10px rgba(200,150,62,0.5)"
      }} />
      {/* Service Detail Popup */}
      {selectedService !== null && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 10000,
          background: "rgba(0,0,0,0.75)", backdropFilter: "blur(8px)",
          display: "flex", alignItems: "center", justifyContent: "center",
          padding: 20, animation: "fadeIn 0.3s ease-out"
        }} onClick={() => setSelectedService(null)}>
          <div style={{
            background: light ? bk : "#1A181E",
            border: "1px solid rgba(200,150,62,0.2)",
            borderRadius: 8, maxWidth: 640, width: "100%",
            maxHeight: "85vh", overflowY: "auto",
            padding: "48px 40px", position: "relative",
            animation: "slideUp 0.4s cubic-bezier(0.16, 1, 0.3, 1)"
          }} onClick={e => e.stopPropagation()}>

            {/* Close button */}
            <button onClick={() => setSelectedService(null)} style={{
              position: "absolute", top: 16, right: r ? "auto" : 16, left: r ? 16 : "auto",
              background: "none", border: "none", color: m, fontSize: 24,
              cursor: "pointer", padding: 8, lineHeight: 1
            }}>✕</button>

            {(() => {
              const s = (r ? serviceDetails.ar : serviceDetails.en)[selectedService];
              return (
                <>
                  <p style={{ fontSize: 12, letterSpacing: ls(6), textTransform: uc, color: g, marginBottom: 12, fontFamily: ff }}>{s.subtitle}</p>
                  <h3 style={{ fontSize: 28, fontWeight: 400, color: ln, marginBottom: 24, fontFamily: ff, letterSpacing: ls(1) }}>{s.title}</h3>
                  <div style={{ width: 50, height: 1, background: g, marginBottom: 28, opacity: 0.5 }} />
                  <p style={{ fontSize: 15, color: m, lineHeight: 1.9, marginBottom: 32, fontFamily: fb }}>{s.desc}</p>

                  <p style={{ fontSize: 12, letterSpacing: ls(4), textTransform: uc, color: g, marginBottom: 16, fontFamily: ff }}>
                    {r ? "المميزات" : "Features"}
                  </p>
                  {s.features.map((f2, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 14 }}>
                      <span style={{ color: g, fontSize: 14, marginTop: 2, flexShrink: 0 }}>◆</span>
                      <p style={{ fontSize: 14, color: ln, lineHeight: 1.7, fontFamily: fb, opacity: 0.85 }}>{f2}</p>
                    </div>
                  ))}

                  <div style={{ marginTop: 32, padding: "20px 24px", background: "rgba(200,150,62,0.06)", border: "1px solid rgba(200,150,62,0.12)", borderRadius: 6 }}>
                    <p style={{ fontSize: 12, letterSpacing: ls(4), textTransform: uc, color: g, marginBottom: 10, fontFamily: ff }}>
                      {r ? "مثال عملي" : "Real Example"}
                    </p>
                    <p style={{ fontSize: 14, color: ln, lineHeight: 1.8, fontFamily: fb, fontStyle: "italic", opacity: 0.8 }}>{s.example}</p>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}
      <KhwarizmChat isRTL={r} />

      
      {/* Back to Top */}
      {showBtt && (
        <button className="btt" onClick={() => window.scrollTo({top:0,behavior:'smooth'})}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#C8963E" strokeWidth="2">
            <path d="M18 15l-6-6-6 6"/>
          </svg>
        </button>
      )}
      <KhwarizmChat isRTL={r} />
    </div>
  );
}
